import {toast} from 'react-toastify';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {updateBrand, updateBrandLogo, VerdocsEndpoint, type IBrand} from '@verdocs/js-sdk';
import {invalidateAllQueries, useBrand} from '../../react-sdk/queries';
import {buildEmailPreviewHtml, writeIframe} from './BrandPreview';
import {BuilderFrame, LeaveDialog} from './BuilderFrame';
import {PhotoSelector, Spinner} from '../../Components';
import {EmailWindowHeader} from './MockChrome';
import {normalizeHex} from './BrandBuilderState';
import {track} from '../../Managers/Analytics';
import {AppState} from '../../AppState';

// The invitation email is a server-rendered Handlebars template, so it takes no CSS overrides:
// email clients ignore stylesheets. Only the brand-record fields below reach it today (the banner
// is primary_color and the CTA is secondary_color, per
// api/src/modules/templates/announcements/recipient:invited.html). Those same fields also paint
// the signing experience, which the UI says out loud rather than pretending they are email-only.
// Everything else the design calls for needs email-templating work on the API first and is marked
// as such instead of being faked.

interface EmailBuilderProps {
  brandId: string;
  onBack: () => void;
  onOpenDevPanel: () => void;
}

interface IEmailDraft {
  senderName: string;
  primary: string;
  secondary: string;
}

const PLANNED = [
  {label: 'Banner color, independent of the signing primary', note: 'Needs a separate field on the brand record.'},
  {label: 'Button color and label, independent of the secondary', note: 'Needs a separate field on the brand record.'},
  {label: 'Custom subject lines and body copy', note: 'Template text is fixed server-side today.'},
  {label: 'Delivery metrics (opens, bounces)', note: 'Awaiting an email-metrics endpoint.'},
];

const ColorField: FC<{label: string; hint: string; value: string; onChange: (v: string) => void}> = ({label, hint, value, onChange}) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);

  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-xs font-medium text-gray-600">{label}</label>
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-1">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-7 w-9 cursor-pointer rounded border-0 bg-transparent p-0"
        />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            const normalized = normalizeHex(text);
            if (normalized) onChange(normalized);
            else setText(value);
          }}
          className="w-full min-w-0 border-0 font-mono text-xs uppercase text-gray-700 focus:outline-none"
        />
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-gray-400">{hint}</p>
    </div>
  );
};

export const EmailBuilder: FC<EmailBuilderProps> = ({brandId, onBack, onOpenDevPanel}) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const brandQuery = useBrand(organizationId, brandId);
  const brand = brandQuery.data;

  const [draft, setDraft] = useState<IEmailDraft | null>(null);
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [working, setWorking] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!brand || draft) return;
    setDraft({
      senderName: brand.email_sender_name || '',
      primary: normalizeHex(brand.primary_color || '') || '#654DCB',
      secondary: normalizeHex(brand.secondary_color || '') || '#55BC81',
    });
  }, [brand, draft]);

  const logoPreview = useMemo(() => (newLogo ? URL.createObjectURL(newLogo) : null), [newLogo]);
  useEffect(() => () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
  }, [logoPreview]);

  const previewBrand: IBrand | null =
    brand && draft
      ? {
          ...brand,
          email_sender_name: draft.senderName.trim() || null,
          primary_color: draft.primary,
          secondary_color: draft.secondary,
          full_logo_url: logoPreview || brand.full_logo_url,
        }
      : null;

  const html = useMemo(() => (previewBrand ? buildEmailPreviewHtml(previewBrand) : ''), [previewBrand]);
  useEffect(() => {
    if (html) writeIframe(iframeRef.current, html);
  }, [html]);

  if (!brand || !draft || !previewBrand) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-white">
        <Spinner mode="dark" />
      </div>
    );
  }

  const dirty =
    draft.senderName !== (brand.email_sender_name || '') ||
    draft.primary !== (normalizeHex(brand.primary_color || '') || '#654DCB') ||
    draft.secondary !== (normalizeHex(brand.secondary_color || '') || '#55BC81') ||
    !!newLogo;

  const handleDiscard = () => {
    setDraft({
      senderName: brand.email_sender_name || '',
      primary: normalizeHex(brand.primary_color || '') || '#654DCB',
      secondary: normalizeHex(brand.secondary_color || '') || '#55BC81',
    });
    setNewLogo(null);
  };

  const leave = (then: () => void) => (dirty ? setPendingLeave(() => then) : then());

  const handlePublish = async () => {
    setWorking(true);
    try {
      // Only the fields this screen owns, so an unpublished Theme Editor draft is left alone.
      await updateBrand(VerdocsEndpoint.getDefault(), organizationId, brand.id, {
        email_sender_name: draft.senderName.trim() || null,
        primary_color: draft.primary,
        secondary_color: draft.secondary,
      });
      if (newLogo) {
        await updateBrandLogo(VerdocsEndpoint.getDefault(), organizationId, brand.id, newLogo);
        setNewLogo(null);
      }
      invalidateAllQueries();
      track('brand_published', {org_id: organizationId, surface: 'email'});
      toast.success('Published. New invitations will use this look.');
    } catch (e: any) {
      toast.error(`Error publishing: ${e.response?.data?.error || e.message}`);
    }
    setWorking(false);
  };

  const logoUrl = logoPreview || brand.thumbnail_url || brand.full_logo_url;

  return (
    <BuilderFrame
      brand={brand}
      name={brand.name || brand.key}
      logoUrl={logoUrl}
      label="Email Builder"
      dirty={dirty}
      working={working}
      onClose={() => leave(onBack)}
      onDiscard={handleDiscard}
      onPublish={handlePublish}>
      <div className="flex min-h-0 flex-1 flex-col gap-4 laptop:flex-row">
        <div className="w-full shrink-0 overflow-y-auto rounded-xl border border-gray-200 p-3.5 laptop:w-[330px]">
          <p className="mb-4 rounded-lg bg-indigo-50/60 px-3 py-2 text-[11px] leading-relaxed text-indigo-900">
            Email clients ignore stylesheets, so invitations are styled from the brand record rather than CSS. The logo and both colors are
            shared with the signing experience: changing them here changes it there too.
          </p>

          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Logo</label>
            <PhotoSelector newFile={newLogo} value={brand.full_logo_url || ''} onSelectFile={setNewLogo} />
            <p className="mt-1 text-[11px] leading-relaxed text-gray-400">Shown at the top of every notification.</p>
          </div>

          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-medium text-gray-600">From name</label>
            <input
              value={draft.senderName}
              placeholder={brand.email_display_name || brand.organization?.name || 'Acme Documents'}
              onChange={(e) => setDraft({...draft, senderName: e.target.value})}
              className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-indigo-400 focus:outline-none"
            />
            <p className="mt-1 text-[11px] leading-relaxed text-gray-400">The name recipients see in their inbox. Empty uses your organization's name.</p>
          </div>

          <ColorField
            label="Banner color"
            hint="The block behind the document icon. This is the brand's primary color."
            value={draft.primary}
            onChange={(v) => setDraft({...draft, primary: v})}
          />
          <ColorField
            label="Button color"
            hint="The Review Document button. This is the brand's secondary color; label text switches to stay readable."
            value={draft.secondary}
            onChange={(v) => setDraft({...draft, secondary: v})}
          />

          <div className="mt-6 border-t border-gray-100 pt-4">
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Coming soon</h3>
            <ul className="mb-3">
              {PLANNED.map((item) => (
                <li key={item.label} className="mb-2.5">
                  <span className="flex items-start gap-2 text-[12px] leading-snug text-gray-400">
                    <span className="mt-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
                      Soon
                    </span>
                    <span>
                      {item.label}
                      <span className="block text-[11px] text-gray-300">{item.note}</span>
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <button onClick={() => leave(onOpenDevPanel)} className="text-xs font-medium text-indigo-600 hover:underline">
              Bring your own HTML template &rarr;
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200">
          <EmailWindowHeader brand={previewBrand} />
          <div className="min-h-0 flex-1 overflow-y-auto p-6" style={{backgroundColor: '#eaeaea'}}>
            <iframe
              ref={iframeRef}
              title="Email preview"
              className="mx-auto block w-full border-0 bg-transparent"
              style={{height: '680px', maxWidth: '700px'}}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>

      {working && (
        <div className="absolute inset-0 flex z-40 bg-[#ffffff7f] items-center justify-center">
          <Spinner mode="dark" />
        </div>
      )}

      {pendingLeave && <LeaveDialog onStay={() => setPendingLeave(null)} onLeave={pendingLeave} />}
    </BuilderFrame>
  );
};
